import { toWireCartOptions } from './cartOptions';
import { saleApi } from '@/app/api/saleApi';
import { hubApi } from '@/app/api/hubApi';
import { channelOfMode, isChannelPaused } from '@/features/restaurant/pausedChannels';
import { backendLanguage } from '@/lib/backendLanguage';
import type { Order, PlaceOrderPayload, SelectedOption } from '@/types';
import type { PaymentChannel, PaymentMethod } from '@/types/order';
import type { OrderHistoryStatus, OrderListEntry } from '@/types/account';
import type { RootState } from '@/app/store';

interface RawSaleOrderItemOption {
  option_value_uuid: string;
  name: string;
  price_delta_ttc: string;
  qty?: number;
}

interface RawSaleOrderItem {
  product_code?: string | null;
  uuid: string;
  product_uuid: string;
  product_name: string;
  qte: number;
  unit_ttc_origin: string;
  total_ttc_after_discount: string;
  comment?: string;
  options?: RawSaleOrderItemOption[];
  // SC-346/SC-238: the dine-in round this item was added in. Null for
  // non-dine-in lines and legacy rows.
  round_number?: number | null;
}

interface RawSaleOrder {
  uuid: string;
  daily_number?: number | null;
  restaurant_uuid: string;
  consumer_name?: string;
  customer_phone?: string;
  customer_email?: string | null;
  type: 'take_out' | 'delivery' | 'dine_in';
  payment_method?: string | null;
  payment_channel?: string | null;
  payment_status: Order['paymentStatus'];
  online_status: Order['status'];
  total_ttc: string;
  delivery_fee_ttc?: string;
  discount_percent?: string;
  discount_amount?: string;
  loyalty_discount_ttc?: string;
  created_at: string;
  // L'heure de retrait/livraison retenue par le serveur. Absente d'une commande
  // sans créneau ; sur place elle vaut le moment même de la commande.
  scheduled_at?: string | null;
  // SC-1500 — « Dès que possible » ; `scheduled_at` reste le créneau le plus proche.
  is_asap?: boolean;
  held_rounds?: number[];
  round_service_labels?: Record<string, string>;
  delivery_address_line1?: string;
  delivery_address_line2?: string;
  delivery_postcode?: string;
  delivery_city?: string;
  items?: RawSaleOrderItem[];
}

interface OnlineOrderResponse {
  order: RawSaleOrder;
  order_access_token?: string | null;
  client_secret: string | null;
  payment_intent_id: string | null;
  provider?: string | null;
  redirect_url?: string | null;
  // Server-computed loyalty redemption discount (decimal string, e.g. "5.00";
  // "0.00" when no redemption_code was sent).
  redemption_discount_ttc?: string;
}

function centsToEuros(cents: number): string {
  return (cents / 100).toFixed(2);
}

function eurosToCents(value: string | undefined): number {
  return Math.round(parseFloat(value || '0') * 100);
}

// Serialise a cart line's selected options to the sale_api wire shape. Exported
// so the shared table cart (SC-169) builds option payloads identically to the
// normal place-order path — option parity across both submit routes.
export function optionEntries(line: { selectedOptions: SelectedOption[] }): { option_value_uuid: string; qty: number }[] {
  return line.selectedOptions.flatMap((group) =>
    group.options.map((o) => ({ option_value_uuid: o.id, qty: o.qty })),
  );
}

// SC-346: the dine-in round a submit just created is the HIGHEST round_number in
// the merged anchor order's items (sale_api derives next_round = max(existing)+1).
// Both dine-in submit paths use this to pin the placed round into the confirmation
// URL so the recap shows only the just-sent dishes, not earlier rounds. Returns
// null when no line carries a round (non-dine-in / legacy).
export function latestRoundNumber(roundNumbers: (number | null | undefined)[]): number | null {
  const rounds = roundNumbers.filter((n): n is number => typeof n === 'number');
  return rounds.length ? Math.max(...rounds) : null;
}

// DF-0586 — an « envoi par service » send creates its first service as a round
// that goes out now and the next ones as held rounds above it, so the highest
// round alone would pin the recap to the desserts. The recap's rounds are then
// the latest round that went out plus every round still held — the value of
// `?round=` as a comma list. Nothing held → exactly `latestRoundNumber`.
export function sentRoundsParam(
  roundNumbers: (number | null | undefined)[],
  heldRounds: number[] | undefined,
): string | null {
  const held = heldRounds ?? [];
  if (held.length === 0) {
    const round = latestRoundNumber(roundNumbers);
    return round == null ? null : String(round);
  }
  const live = latestRoundNumber(roundNumbers.filter((n) => typeof n === 'number' && !held.includes(n)));
  const rounds = [...new Set([...(live == null ? [] : [live]), ...held])].sort((a, b) => a - b);
  return rounds.join(',');
}

// Exported so the waiter ticket's round-confirm flow (`waiterApi.ts`'s
// `postRound`) can reuse the identical sessionStorage single-flight caching
// instead of re-implementing it — namespaced by SESSION uuid there instead of
// restaurant uuid (spec §1.7 "Idempotency-Key des tournées par session —
// aujourd'hui par restaurant → 409 croisés"): two open tickets on the same
// restaurant must never collide on one key.
export function idempotencyKey(restaurantUuid: string): string {
  const key = `online-order-idempotency:${restaurantUuid}`;
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const value = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(key, value);
  return value;
}

export function clearIdempotencyKey(restaurantUuid: string): void {
  sessionStorage.removeItem(`online-order-idempotency:${restaurantUuid}`);
}

export function orderAccessTokenKey(orderId: string): string {
  return `online-order-access:${orderId}`;
}

export function persistOrderAccessToken(orderId: string, token?: string | null): void {
  if (!token || typeof window === 'undefined') return;
  const key = orderAccessTokenKey(orderId);
  sessionStorage.setItem(key, token);
  localStorage.setItem(key, token);
}

export function readOrderAccessToken(orderId: string): string | null {
  if (typeof window === 'undefined') return null;
  const urlToken = new URLSearchParams(window.location.search).get('access_token');
  if (urlToken) {
    persistOrderAccessToken(orderId, urlToken);
    return urlToken;
  }
  const key = orderAccessTokenKey(orderId);
  return sessionStorage.getItem(key) ?? localStorage.getItem(key);
}

function transformRawOrder(
  raw: RawSaleOrder,
  payment: {
    clientSecret: string | null;
    paymentIntentId: string | null;
    orderAccessToken?: string | null;
    provider?: string | null;
    redirectUrl?: string | null;
    redemptionDiscountTtc?: string;
  } = { clientSecret: null, paymentIntentId: null, orderAccessToken: null },
  fallback?: PlaceOrderPayload,
): Order {
  const created = raw.created_at || new Date().toISOString();
  // SC-879 — l'heure annoncée au client est celle qu'il a choisie et que sale_api a
  // validée, pas un +25 min forfaitaire : l'horloge peut avoir avancé le créneau
  // entre le choix et l'envoi (cf. `resolveScheduledAtNow`), et aucun écran ne
  // disait alors l'heure réelle. Le forfait reste le repli pour le sur place
  // (`scheduled_at` y vaut l'instant de l'envoi, qui n'annonce rien) et pour une
  // commande sans créneau.
  const fallbackEta = new Date(new Date(created).getTime() + 25 * 60 * 1000).toISOString();
  const eta = raw.type !== 'dine_in' && raw.scheduled_at ? raw.scheduled_at : fallbackEta;
  const lines = raw.items?.map((item) => {
    const opts = item.options ?? [];
    // Backend persists resolved option values per line but loses the original
    // group structure. Rebuild a single synthetic group so Recommander still
    // submits the same option UUIDs; the menu's groupId→label lookup won't
    // resolve them, hence we also denormalize the display names via optionNames.
    const selectedOptions = opts.length
      ? [{ groupId: '', options: opts.map((o) => ({ id: o.option_value_uuid, qty: Math.max(1, Number(o.qty) || 1) })) }]
      : [];
    const optionNames = opts.length
      ? opts.map((o) => {
          const qty = Math.max(1, Number(o.qty) || 1);
          return qty > 1 ? `${qty}× ${o.name}` : o.name;
        })
      : undefined;
    return {
      lineId: item.uuid,
      itemId: item.product_uuid,
      qty: item.qte,
      selectedOptions,
      unitPrice: Math.round(parseFloat(item.unit_ttc_origin || '0') * 100),
      note: item.comment || undefined,
      snapshot: { name: item.product_name, photo: '', code: item.product_code ?? undefined },
      optionNames,
      roundNumber: item.round_number ?? null,
    };
  }) ?? fallback?.lines ?? [];

  return {
    orderId: raw.uuid,
    dailyNumber: raw.daily_number ?? null,
    restaurantUuid: raw.restaurant_uuid,
    customer: {
      name: raw.consumer_name || fallback?.customer.name || '',
      phone: raw.customer_phone || fallback?.customer.phone || '',
      email: raw.customer_email || fallback?.customer.email || '',
    },
    mode: raw.type === 'delivery' ? 'delivery' : 'takeaway',
    address: raw.type === 'delivery'
      ? {
          street: raw.delivery_address_line1 || fallback?.address?.street || '',
          city: raw.delivery_city || fallback?.address?.city || '',
          zip: raw.delivery_postcode || fallback?.address?.zip || '',
          instructions: raw.delivery_address_line2 || fallback?.address?.instructions || '',
        }
      : undefined,
    scheduledAt: raw.scheduled_at ?? fallback?.scheduledAt ?? '',
    ...(raw.is_asap ? { isAsap: true } : {}),
    payment: {
      channel: (raw.payment_channel === 'on_site' ? 'on_site' : 'online') as PaymentChannel,
      method: (raw.payment_method ?? null) as PaymentMethod | null,
    },
    paymentStatus: raw.payment_status,
    clientSecret: payment.clientSecret,
    paymentIntentId: payment.paymentIntentId,
    orderAccessToken: payment.orderAccessToken,
    provider: payment.provider,
    redirectUrl: payment.redirectUrl,
    redemptionDiscountTtc: payment.redemptionDiscountTtc,
    lines,
    totals: fallback?.totals ?? {
      subtotal:
        eurosToCents(raw.total_ttc) -
        eurosToCents(raw.delivery_fee_ttc) +
        eurosToCents(raw.discount_amount ?? '0') +
        eurosToCents(raw.loyalty_discount_ttc),
      deliveryFee: eurosToCents(raw.delivery_fee_ttc),
      total: eurosToCents(raw.total_ttc),
      discountAmountCents: eurosToCents(raw.discount_amount ?? '0'),
      discountPercent: Math.round(parseFloat(raw.discount_percent ?? '0')),
      loyaltyDiscountCents: eurosToCents(raw.loyalty_discount_ttc),
    },
    status: raw.online_status,
    createdAt: created,
    estimatedReadyAt: eta,
    ...(raw.held_rounds?.length
      ? { heldRounds: raw.held_rounds, roundServiceLabels: raw.round_service_labels ?? {} }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Consumer order list types + adapter
// ---------------------------------------------------------------------------

export interface RawConsumerOrderListItem {
  uuid: string;
  restaurant_uuid: string;
  type: 'delivery' | 'take_out';
  online_status: string;
  payment_status: string;
  total_ttc: string;
  delivery_fee_ttc: string;
  created_at: string;
  delivery_address_line1?: string;
  delivery_postcode?: string;
  delivery_city?: string;
  line_count: number;
  first_item_name: string;
}

export interface ConsumerOrderListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: RawConsumerOrderListItem[];
}

export interface GetMyOrdersArgs {
  restaurantUuid: string;
  status?: string;
  since?: string;
  until?: string;
  page: number;
  pageSize: number;
}

export function mapBackendStatusToHistory(s: string): OrderHistoryStatus {
  if (s === 'ready' || s === 'handed_over') return 'completed';
  if (s === 'cancelled') return 'cancelled';
  return 'in-delivery';
}

export function transformConsumerListEntry(raw: RawConsumerOrderListItem): OrderListEntry {
  return {
    id: raw.uuid,
    placedAt: raw.created_at,
    mode: raw.type === 'delivery' ? 'delivery' : 'takeaway',
    status: mapBackendStatusToHistory(raw.online_status),
    total: Math.round(parseFloat(raw.total_ttc || '0') * 100),
    lineCount: raw.line_count,
    addressLabel: raw.delivery_address_line1 || undefined,
    firstItemName: raw.first_item_name || '',
  };
}

export const ordersApi = saleApi.injectEndpoints({
  endpoints: (build) => ({
    placeOrder: build.mutation<Order, PlaceOrderPayload>({
      queryFn: async (payload, api, _extraOptions, baseQuery) => {
        const state = api.getState() as RootState;
        const cached = hubApi.endpoints.getRestaurantIdentity.select(payload.restaurantUuid)(state);
        const id = cached.data;
        if (id && isChannelPaused(id, channelOfMode(payload.mode))) {
          return { error: { status: 409, data: 'closed' } as never };
        }
        const idem = idempotencyKey(payload.restaurantUuid);
        const note = payload.customerNote?.trim() || '';
        const cartOptions = toWireCartOptions(payload.cartOptions ?? []);
        const isDineIn = payload.mode === 'dine_in';

        // Build request headers — dine_in sends Bearer guest token instead of
        // the consumer Token (which the base query injects via JWT interceptor).
        const requestHeaders: Record<string, string> = { 'Idempotency-Key': idem };
        if (isDineIn && payload.guestToken) {
          requestHeaders['Authorization'] = `Bearer ${payload.guestToken}`;
        }

        const result = await baseQuery({
          url: '/sale/order/order/online/',
          method: 'POST',
          headers: requestHeaders,
          body: {
            restaurant_uuid: payload.restaurantUuid,
            type: isDineIn
              ? 'dine_in'
              : payload.mode === 'delivery'
                ? 'delivery'
                : 'take_out',
            // dine_in forces on_site payment (v1)
            payment_channel: isDineIn ? 'on_site' : payload.payment.channel,
            customer: {
              name: payload.customer.name.trim(),
              // phone/email are optional for dine_in (skip if blank)
              ...(isDineIn
                ? {
                    ...(payload.customer.phone.trim()
                      ? { phone: payload.customer.phone.trim() }
                      : {}),
                    ...(payload.customer.email.trim()
                      ? { email: payload.customer.email.trim() }
                      : {}),
                  }
                : {
                    phone: payload.customer.phone.trim(),
                    email: payload.customer.email.trim(),
                  }),
            },
            // DF-0574 — ja/ar UI: sale_api refuses them, the order is lost.
            language: backendLanguage(payload.language),
            ...(note ? { customer_note: note } : {}),
            // SC-739 — omis quand rien n'est coché : une clé vide ferait
            // travailler le devis pour rien.
            ...(cartOptions.length ? { cart_options: cartOptions } : {}),
            // DF-0586 — le choix de service de la table, seulement s'il a été posé.
            ...(isDineIn && payload.serviceChoice ? { service_choice: payload.serviceChoice } : {}),
            // dine_in skips address and scheduled_at
            ...(!isDineIn && {
              address: payload.address
                ? {
                    line1: payload.address.street,
                    line2: payload.address.instructions ?? '',
                    postcode: payload.address.zip,
                    city: payload.address.city,
                  }
                : undefined,
              // For DELIVERY orders, the server requires the geocoded destination
              // and recomputes the fee server-side from DeliveryConfig+Routes.
              delivery_destination:
                payload.mode === 'delivery' &&
                payload.address?.place_id &&
                payload.address.lat != null &&
                payload.address.lng != null
                  ? {
                      place_id: payload.address.place_id,
                      lat: payload.address.lat,
                      lng: payload.address.lng,
                      formatted_address: payload.address.formatted_address ?? '',
                    }
                  : undefined,
              scheduled_at: payload.scheduledAt,
              ...(payload.isAsap ? { is_asap: true } : {}),
            }),
            lines: payload.lines.map((line) => ({
              product_uuid: line.itemId,
              quantity: line.qty,
              options: optionEntries(line),
              comment: line.note ?? '',
            })),
            // Loyalty redemption (SC-1091): when the advantage's type and value are
            // known (wallet coupon / exchanged reward) `totals.total` already holds
            // the exact post-discount total and is sent like any other — the server
            // compares it to the cent, so the customer is never charged an amount
            // different from the one shown (`total_mismatch` otherwise). Only a
            // code typed by hand and unknown to the wallet has no value to price:
            // total_ttc is omitted and the server computes the discount alone.
            ...(payload.redemptionCode && !payload.redemptionKnown
              ? {}
              : { total_ttc: centsToEuros(payload.totals.total) }),
            // delivery_fee_ttc is sent for the server's total_ttc cross-check
            // but the server overrides it from its own compute_quote output.
            delivery_fee_ttc: centsToEuros(payload.totals.deliveryFee),
            idempotency_key: idem,
            // Cloudflare Turnstile token — only online (take_out/delivery) orders
            // are captcha-gated server-side; dine_in is exempt (table-session JWT).
            ...(isDineIn ? {} : { recaptcha_token: payload.recaptchaToken ?? '' }),
            ...(payload.redemptionCode ? { redemption_code: payload.redemptionCode } : {}),
          },
        });
        if (result.error) return { error: result.error as never };
        const data = result.data as OnlineOrderResponse;
        const order = transformRawOrder(
          data.order,
          {
            clientSecret: data.client_secret,
            paymentIntentId: data.payment_intent_id,
            orderAccessToken: data.order_access_token,
            provider: data.provider,
            redirectUrl: data.redirect_url,
            redemptionDiscountTtc: data.redemption_discount_ttc,
          },
          payload,
        );
        persistOrderAccessToken(order.orderId, data.order_access_token);
        clearIdempotencyKey(payload.restaurantUuid);
        return { data: order };
      },
      invalidatesTags: (_result, _error, payload) =>
        payload.mode === 'dine_in' ? ['Order', 'TableSession'] : ['Order'],
    }),
    getOrder: build.query<Order, string>({
      async queryFn(orderId, _api, _extraOptions, baseQuery) {
        const token = readOrderAccessToken(orderId);
        const query = token ? `?access_token=${encodeURIComponent(token)}` : '';
        const result = await baseQuery(`/sale/order/order/${orderId}/${query}`);
        if (result.error) return { error: result.error as never };
        return { data: transformRawOrder(result.data as RawSaleOrder) };
      },
      providesTags: (_r, _e, id) => [{ type: 'Order', id }],
    }),
    getMyOrders: build.query<ConsumerOrderListResponse, GetMyOrdersArgs>({
      query: ({ restaurantUuid, status, since, until, page, pageSize }) => {
        const params = new URLSearchParams();
        params.set('restaurant_uuid', restaurantUuid);
        params.set('page', String(page));
        params.set('page_size', String(pageSize));
        if (status) params.set('status', status);
        if (since) params.set('since', since);
        if (until) params.set('until', until);
        return `/sale/order/order/mine/?${params.toString()}`;
      },
      providesTags: ['Order'],
    }),
  }),
});

export const { usePlaceOrderMutation, useGetOrderQuery, useGetMyOrdersQuery } = ordersApi;
